import { consola } from 'consola'
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { toSelectedDeliveryAddress } from '../lib/selectedDeliveryAddress';
import { formatPhoneNumber, setMonitoringUser } from '@shared/lib';
import { useSessionManager, useAuthState } from '~/entities/auth/@x/user';
import { useInstitutionStore, findBranchAtPoint } from '~/entities/institution/@x/user';
import apiProfileInstance from '~/entities/user/api';
import type { User, UserAddress, SelectedDeliveryAddress } from '@/utils/types/user';
import { useLoyaltyStore } from '~/entities/loyalty/@x/user';

// Константы для улучшения читаемости
const SECURE_STORAGE_RESTORE_DELAY_MS = 2000;
const TOKEN_MANAGER_FALLBACK_DELAY_MS = 100;

export const useUserProfileStore = defineStore('userProfile', () => {

  /* ─────────────────── state ─────────────────── */
  const sessionManager = useSessionManager();
  const { isLoggedIn } = useAuthState();
  const loyaltyStore = useLoyaltyStore();

  const userId = ref<number | null>(null);
  // Координаты — часть адреса, а не отдельное эфемерное состояние: по ним бэкенд
  // авторитетно подбирает филиал при оформлении. Пока они жили только в
  // checkoutFormStore.deliveryCoords (не переживает перезапуск приложения),
  // пользователь с сохранённым адресом упирался в DELIVERY_COORDS_REQUIRED.
  const selectedDeliveryAddress = ref<SelectedDeliveryAddress | null>(null);
  // Завершено ли восстановление дефолтного адреса из localStorage/SecureStorage.
  // До этого момента нельзя слать /orders/calculate для доставки: зона ещё не подтянулась
  // и бэкенд вернёт «укажите адрес» на заведомо неполном запросе (мигающий тост).
  const isDeliveryAddressRestored = ref(false);

  // Новое хранилище для адресов из API v2
  const userAddresses = ref<UserAddress[]>([]);

  const user = ref<User>({
    id: 0,
    first_name: '',
    last_name: '',
    default_phone_number: '',
    default_email: '',
    birthday: new Date(),
    sex: '',
    delivery_address: [],
    is_email_verified: false,
    is_phone_verified: false,
    has_password: false,
    points: 0,
    cashback_level: '3%',
    total_spent_current_period: 0,
    points_expiry_date: null,
  });


  if (process.client) {
    const id = sessionStorage.getItem('userId');
    userId.value = id ? Number(id) : null;
  }

  setTimeout(async () => {
    /** Восстанавливаем адрес ПОСЛЕ гидратации */
    if (process.client) {
      try {
        const { useDeliveryAddressStorage } = await import('./useDeliveryAddressStorage');
        const storage = useDeliveryAddressStorage();

        const savedAddress = await storage.getDeliveryAddress();
        if (savedAddress) {
          // Координаты филиала в записи — след старого окна карты (#8300): строка
          // хутора вне зон, а точка — филиала, на котором открылась карта. Такая
          // запись пережила бы и фикс, и чистку базы, и первый же заказ снова ушёл
          // бы в зону филиала. Точку снимаем, строку оставляем: координаты доберутся
          // из «Моих адресов», а иначе сервер попросит указать дом на карте.
          const restored = dropBranchPointCoords(savedAddress);
          selectedDeliveryAddress.value = restored;
          if (restored !== savedAddress) {
            await storage.setDeliveryAddress(restored);
          }
          // Запись могла быть сделана старой версией клиента — без координат.
          // Если список адресов уже подъехал, добираем их сразу (иначе добор
          // произойдёт в setUserAddresses, когда список загрузится).
          backfillSelectedAddressCoords();
        }
      } catch {
      } finally {
        // Адрес либо восстановлен, либо его нет — в любом случае гидратация завершена,
        // теперь preview-расчёту разрешено стартовать (см. useOrderPreview).
        isDeliveryAddressRestored.value = true;
      }
    }
  }, SECURE_STORAGE_RESTORE_DELAY_MS)


  /* ─────────────────── mutations ─────────────────── */
  const setUser = (data: Partial<User>): void => {
    // Нормализуем адреса доставки
    const normalizedAddresses = normalizeDeliveryAddresses(data.delivery_address);

    // Форматируем номер телефона перед сохранением в store
    const formattedPhone = data.default_phone_number
      ? formatPhoneNumber(data.default_phone_number)
      : data.default_phone_number;

    // ✅ НОВАЯ ЛОГИКА: Если подтверждение телефона через Telegram не требуется,
    // автоматически считаем телефон подтвержденным
    const institutionStore = useInstitutionStore();
    const requiresTelegramVerification = institutionStore.institution?.telegram_verification_required ?? true;

    let phoneVerified = data.is_phone_verified ?? user.value.is_phone_verified;

    // Если подтверждение не требуется настройками заведения - устанавливаем is_phone_verified = true
    if (!requiresTelegramVerification) {
      phoneVerified = true;
    }

    // Обновляем данные пользователя
    user.value = {
      ...user.value,
      ...data,
      default_phone_number: formattedPhone || user.value.default_phone_number,
      delivery_address: normalizedAddresses,
      is_phone_verified: phoneVerified,
    };

    // Обновляем ID пользователя и sessionStorage если необходимо
    updateUserIdAndStorage(data.id);
  };

  /**
   * Нормализует адреса доставки в единый формат
   */
  const normalizeDeliveryAddresses = (deliveryAddress: unknown): Array<{ address: string; zona: string | null }> => {
    // Если это массив адресов
    if (Array.isArray(deliveryAddress)) {
      return deliveryAddress.map(normalizeSingleAddress);
    }

    // Если это одиночный объект адреса
    if (deliveryAddress && typeof deliveryAddress === 'object') {
      return [normalizeSingleAddress(deliveryAddress as Record<string, unknown>)];
    }

    // Если адрес отсутствует или неверного формата
    return [];
  };

  /**
   * Нормализует один адрес доставки
   */
  const normalizeSingleAddress = (address: Record<string, unknown>): { address: string; zona: string | null } => {
    return {
      address: String(address?.address ?? ''),
      zona: address?.zona == null ? null : String(address.zona),
    };
  };

  /**
   * Обновляет ID пользователя и сохраняет в sessionStorage
   */
  const updateUserIdAndStorage = (id: number | undefined): void => {
    if (!id) return;

    userId.value = id;

    if (process.client) {
      sessionStorage.setItem('userId', String(id));
      // Ошибки в GlitchTip помечаются этим id — иначе в тикете поддержки
      // не показать, какие баги ловил конкретно этот гость
      void setMonitoringUser(id);
    }
  };

  const setDeliveryAddress = (addressObj: any, idx = 0) => {
    if (!Array.isArray(user.value.delivery_address)) user.value.delivery_address = [];
    const clone = [...user.value.delivery_address];
    clone[idx] = { ...addressObj };
    user.value.delivery_address = clone;
  };

  /** Выбранный адрес + трассировка */
  const setSelectedDeliveryAddress = async (obj: SelectedDeliveryAddress) => {
    selectedDeliveryAddress.value = obj;

    if (process.client) {
      try {
        const { useDeliveryAddressStorage } = await import('./useDeliveryAddressStorage');
        const storage = useDeliveryAddressStorage();
        await storage.setDeliveryAddress(obj);
      } catch (error) {
        consola.error('[userProfile] Ошибка сохранения адреса:', error);
      }
    }
  };

  /**
   * Снимает с выбранного адреса координаты и зону, оставляя саму строку.
   *
   * Зачем: сервер отвечает DELIVERY_COORDS_REQUIRED / DELIVERY_ADDRESS_INCOMPLETE /
   * DELIVERY_ADDRESS_UNCONFIRMED, когда присланная точка негодна — это координаты
   * филиала или центра населённого пункта из старой записи на устройстве. Клиент
   * такую точку от настоящего дома отличить не может (в записи нет ни компонентов
   * адреса, ни точности геокодера), поэтому чистка базы до устройства не доходит:
   * гость открывал карту, закрывал её и упирался в тот же отказ снова и снова.
   * Снимаем точку — и следующий маркер на карте запишется поверх пустоты.
   */
  const dropSelectedAddressCoords = async () => {
    const selected = selectedDeliveryAddress.value;
    if (!selected) return;
    if (selected.latitude == null && selected.longitude == null && !selected.zona) return;

    consola.warn('📍 [userProfile] Сервер отклонил точку адреса — координаты сняты', {
      address: selected.address,
    });
    await setSelectedDeliveryAddress({ ...selected, latitude: null, longitude: null, zona: null });
  };

  const clearSelectedDeliveryAddress = async () => {
    selectedDeliveryAddress.value = null;

    if (process.client) {
      try {
        const { useDeliveryAddressStorage } = await import('./useDeliveryAddressStorage');
        const storage = useDeliveryAddressStorage();
        await storage.removeDeliveryAddress();
      } catch (error) {
        consola.error('[userProfile] Ошибка очистки адреса:', error);
      }
    }
  };

  const clearUser = () => {

    user.value = {
      ...user.value,
      id: 0,
      first_name: '',
      last_name: '',
      default_phone_number: '',
      default_email: '',
      birthday: new Date(),
      sex: '',
      delivery_address: [],
      is_email_verified: false,
      is_phone_verified: false,
      has_password: false,
      points: 0,
      cashback_level: '3%',
      total_spent_current_period: 0,
      points_expiry_date: null,
    };

    // Очищаем также адреса из API v2
    userAddresses.value = [];
    // Сбрасываем гард авто-подстановки, чтобы при новом входе попытка повторилась.
    defaultAddressResolveAttempted = false;

    clearSelectedDeliveryAddress();
  };

  /**
   * Устанавливает адреса пользователя из API v2
   */
  const setUserAddresses = (addresses: UserAddress[]): void => {
    consola.debug(`📍 [userProfile.setUserAddresses] Устанавливаем ${addresses.length} адресов`);
    consola.debug(`📍 [userProfile.setUserAddresses] Addresses:`, addresses);
    userAddresses.value = addresses;
    consola.debug(`📍 [userProfile.setUserAddresses] Установлено в store:`, userAddresses.value);
    backfillSelectedAddressCoords();
  };

  /**
   * Снимает с восстановленного адреса координаты, совпадающие с координатами филиала.
   * Возвращает тот же объект, если снимать нечего (или филиалы ещё не загружены —
   * тогда точку отсеет pickDeliveryCoords перед отправкой заказа).
   */
  const dropBranchPointCoords = (saved: SelectedDeliveryAddress): SelectedDeliveryAddress => {
    const institutionStore = useInstitutionStore();
    const branchAtPoint = findBranchAtPoint(saved.latitude, saved.longitude, institutionStore.branches);
    if (!branchAtPoint) return saved;
    consola.warn('📍 [userProfile] Восстановленный адрес стоит на координатах филиала — координаты сняты', {
      address: saved.address,
      branch: branchAtPoint.name,
    });
    return { ...saved, latitude: null, longitude: null };
  };

  /**
   * Дополняет выбранный адрес координатами из списка /users/addresses.
   *
   * Нужно для записей, сохранённых старой версией клиента: там в хранилище лежат
   * только строка и зона, а без координат заказ на доставку не оформить. Ищем по
   * точному совпадению строки — совпало, значит это тот же адрес.
   */
  const backfillSelectedAddressCoords = (): void => {
    const selected = selectedDeliveryAddress.value;
    if (!selected) return;
    if (selected.latitude != null && selected.longitude != null) return;

    const normalize = (value: string) => value.trim().replaceAll(/\s+/g, ' ').toLowerCase();
    const wanted = normalize(selected.address ?? '');
    if (!wanted) return;

    const match = userAddresses.value.find(
      (addr) =>
        addr.isActive &&
        addr.latitude != null &&
        addr.longitude != null &&
        normalize(addr.address) === wanted,
    );
    if (!match) return;

    consola.debug('📍 [userProfile] Дополнили выбранный адрес координатами из API');
    void setSelectedDeliveryAddress({
      ...selected,
      latitude: match.latitude,
      longitude: match.longitude,
    });
  };

  /**
   * Получает адреса пользователя из API v2
   */
  const fetchUserAddresses = async (): Promise<void> => {
    // Проверяем авторизацию
    if (!isLoggedIn.value) {
      consola.warn('[fetchUserAddresses] Пользователь не авторизован');
      return;
    }

    try {
      consola.debug('🔄 [fetchUserAddresses] Начинаем загрузку адресов...');
      const addresses = await apiProfileInstance.getUserAddresses();
      consola.debug('✅ [fetchUserAddresses] Получено адресов:', addresses.length);
      setUserAddresses(addresses);
      consola.debug('✅ [fetchUserAddresses] Адреса успешно сохранены в store');
    } catch (error: any) {
      consola.error('❌ [fetchUserAddresses] Ошибка загрузки адресов:', error?.message);
      consola.error('❌ [fetchUserAddresses] Full error:', error);
      // Не бросаем ошибку, чтобы не прерывать работу приложения
    }
  };

  // Гард: пробуем подтянуть список адресов для авто-подстановки максимум один раз,
  // чтобы не дёргать /users/addresses на каждом фоновом пересчёте корзины.
  let defaultAddressResolveAttempted = false;

  /**
   * Гарантирует выбранный адрес доставки, если он ещё не задан.
   *
   * Сценарий: пользователь выбрал «Доставку», но selectedDeliveryAddress пуст
   * (свежее устройство / очищенное хранилище). Вместо ошибки бэкенда
   * DELIVERY_ADDRESS_REQUIRED пытаемся молча подставить ИЗБРАННЫЙ (дефолтный) адрес
   * из /users/addresses. Список уже загружен при логине (fetchUserAddresses в
   * ensureValidToken), поэтому в норме лишнего запроса не происходит — фолбэк-запрос
   * делается не более одного раза.
   *
   * @returns true — адрес уже есть или успешно подставлен; false — избранного адреса нет.
   */
  const ensureDefaultDeliveryAddress = async (): Promise<boolean> => {
    // Адрес с зоной уже выбран — делать нечего.
    if (selectedDeliveryAddress.value?.zona != null) return true;
    if (!isLoggedIn.value) return false;

    // Список адресов ещё не загружен — подтягиваем один раз (фолбэк).
    if (userAddresses.value.length === 0 && !defaultAddressResolveAttempted) {
      defaultAddressResolveAttempted = true;
      await fetchUserAddresses();
    }

    // Ищем избранный (дефолтный) активный адрес с валидной зоной.
    const favorite = userAddresses.value.find(
      (addr) => addr.isDefault && addr.isActive && addr.zona != null && String(addr.zona) !== '',
    );
    if (!favorite) return false;

    await setSelectedDeliveryAddress(toSelectedDeliveryAddress(favorite));
    return true;
  };

  /* ─────────────────── actions ─────────────────── */

  // 🔥 GUARD: храним промис текущей загрузки, чтобы параллельные вызовы ДОЖИДАЛИСЬ её,
  // а не выходили сразу (иначе вызывающий код продолжит с ещё не загруженным userId — гонка,
  // из-за которой, например, история заказов не успевала отправить запрос).
  let fetchUserPromise: Promise<void> | null = null;

  const fetchUser = (): Promise<void> => {
    // Если загрузка уже идёт — возвращаем тот же промис (ждём завершения), а не игнорируем.
    if (fetchUserPromise) {
      return fetchUserPromise;
    }

    fetchUserPromise = (async () => {
      try {
        // Восстанавливаем токены если пользователь не авторизован
        if (!isLoggedIn.value && process.client) {
          await restoreTokensFromSecureStorage();
        }

        // Дополнительная проверка через tokenManager
        if (!isLoggedIn.value) {
          await tryTokenManagerFallback();
        }

        // Если все еще не авторизован, выходим
        if (!isLoggedIn.value) {
          return;
        }

        // Проверяем и обновляем токены, получаем данные пользователя
        await ensureValidToken();
      } finally {
        fetchUserPromise = null;
      }
    })();

    return fetchUserPromise;
  };

  /**
   * Восстанавливает токены из SecureStorage для нативных платформ
   */
  const restoreTokensFromSecureStorage = async (): Promise<void> => {
    try {
      const { Capacitor } = await import('@capacitor/core');

      if (!Capacitor.isNativePlatform()) {
        return;
      }

      const { SecureStoragePlugin: _SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');

      const hasRestoredTokens = await checkSecureStorageTokens();

      if (hasRestoredTokens) {
        await syncTokensWithTokenManager();
      }
    } catch (error) {
      // Логируем ошибку, но не прерываем выполнение
      consola.warn('[userProfile] Ошибка восстановления токенов из SecureStorage:', error);
    }
  };

  /**
   * Проверяет наличие токенов в SecureStorage
   */
  const checkSecureStorageTokens = async (): Promise<boolean> => {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');

    let hasRestoredTokens = false;

    // Проверяем access token
    if (!sessionManager.authAccessToken.value) {
      const accessResult = await SecureStoragePlugin.get({ key: 'authAccessToken' });
      if (accessResult?.value) {
        hasRestoredTokens = true;
      }
    }

    // Проверяем refresh token
    if (!sessionManager.authRefreshToken.value) {
      const refreshResult = await SecureStoragePlugin.get({ key: 'authRefreshToken' });
      if (refreshResult?.value) {
        hasRestoredTokens = true;
      }
    }

    return hasRestoredTokens;
  };

  /**
   * Синхронизирует сессию из SecureStorage с sessionManager (V2)
   */
  const syncTokensWithTokenManager = async (): Promise<void> => {
    try {
      const { useSessionManager } = await import('~/entities/auth/@x/user');
      const sessionManager = useSessionManager();

      // V2: Восстанавливаем сессию из SecureStorage
      const restored = await sessionManager.restoreSessionFromStorage();

      if (restored) {
        consola.debug('[userProfile] Сессия восстановлена из SecureStorage');
      }
    } catch (error) {
      consola.warn('[userProfile] Ошибка синхронизации сессии:', error);
    }
  };

  /**
   * Пытается восстановить сессию через sessionManager (V2)
   */
  const tryTokenManagerFallback = async (): Promise<void> => {
    try {
      const { useSessionManager } = await import('~/entities/auth/@x/user');
      const sessionManager = useSessionManager();

      // V2: Пытаемся восстановить сессию из SecureStorage
      const restored = await sessionManager.restoreSessionFromStorage();

      if (restored && sessionManager.hasActiveSession.value) {
        // Ждем обновления computed свойств
        await waitForComputedUpdate();

        // Проверяем, что авторизация прошла успешно
        if (!isLoggedIn.value) {
          consola.warn('[userProfile] Сессия восстановлена, но авторизация не прошла');
        }
      }
    } catch (error) {
      consola.warn('[userProfile] Ошибка fallback восстановления сессии:', error);
    }
  };

  /**
   * Ждет обновления computed свойств после восстановления токенов
   */
  const waitForComputedUpdate = async (): Promise<void> => {
    await new Promise(resolve => setTimeout(resolve, TOKEN_MANAGER_FALLBACK_DELAY_MS));
  };

  /**
   * Обеспечивает наличие валидного токена и загружает данные пользователя
   *
   * ✅ УЛУЧШЕНО: Убрана дублирующая обработка 401
   * ✅ useApi автоматически обрабатывает 401 и делает retry
   * ✅ Нет необходимости в ручном retry и задержках
   */
  const ensureValidToken = async (): Promise<boolean> => {
    // ✅ Проверяем наличие токена (НЕ обновляем вручную!)
    // useApi сам обработает 401 и обновит токен если нужно
    if (!sessionManager.authAccessToken.value) {
      consola.warn('[ensureValidToken] Нет access token, пропускаем загрузку пользователя');
      return false;
    }

    // Получаем данные пользователя
    try {
      const userData = await fetchUserData();
      if (userData) {
        setUser(userData);

        // Загружаем адреса пользователя из API v2
        await fetchUserAddresses();

        await loyaltyStore.fetchLoyaltyInfo();

        return true;
      }
    } catch (error: any) {
      // ✅ УБРАНО: handleUserDataError - дублирующая обработка 401
      // useApi уже сделал retry, если ошибка осталась - значит токен действительно недействителен
      consola.warn('[ensureValidToken] Ошибка загрузки пользователя:', error?.status, error?.message);
      return false;
    }

    return false;
  };

  /**
   * Получает данные пользователя с API
   *
   * ✅ УПРОЩЕНО: Убрана дублирующая обработка ошибок
   * ✅ useApi автоматически обрабатывает 401 и делает retry
   */
  const fetchUserData = async (): Promise<User | null> => {
    try {
      const userData = await apiProfileInstance.getUser();
      return userData && userData.id ? userData : null;
    } catch (error) {
      consola.warn('[fetchUserData] Ошибка получения данных пользователя:', error);
      throw error;
    }
  };

  // ✅ УДАЛЕНО: ensureValidAccessToken - избыточная логика
  // ✅ УДАЛЕНО: handleUserDataError - дублирует обработку 401 из useApi
  // ✅ УДАЛЕНО: isAuthError - больше не нужен
  // ✅ УДАЛЕНО: waitForCookiesUpdate - избыточная задержка 500ms


  return {
    user,
    userId,
    selectedDeliveryAddress,
    isDeliveryAddressRestored,
    userAddresses,

    setUser,
    setDeliveryAddress,
    setSelectedDeliveryAddress,
    clearSelectedDeliveryAddress,
    dropSelectedAddressCoords,
    clearUser,
    setUserAddresses,

    fetchUser,
    fetchUserAddresses,
    ensureDefaultDeliveryAddress,

    getUserId: computed(() => userId.value),
    getUser: computed(() => user.value),
    getSelectedDeliveryAddress: computed(() => selectedDeliveryAddress.value),
    getUserAddresses: computed(() => userAddresses.value),
  };
});

